import { makeRedirectUri } from 'expo-auth-session';
import * as Facebook from 'expo-auth-session/providers/facebook';

import { facebookConfig } from '@/src/config/env';
import { UserFacingError } from '@/src/utils/errors';

export type FacebookPrefillProfile = {
  accessToken: string;
  email?: string;
  fullName?: string;
  photoURL?: string;
};

type FacebookGraphProfile = {
  email?: unknown;
  name?: unknown;
  picture?: {
    data?: {
      url?: unknown;
    };
  };
};

function cleanProfileString(value: unknown, maxLength: number) {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();

  if (!trimmed) {
    return undefined;
  }

  return trimmed.slice(0, maxLength);
}

async function fetchFacebookPrefillProfile(
  accessToken: string,
): Promise<FacebookPrefillProfile> {
  const response = await fetch(
    `https://graph.facebook.com/me?fields=id,name,email,picture.type(large)&access_token=${encodeURIComponent(
      accessToken,
    )}`,
  );

  if (!response.ok) {
    throw new Error('Facebook profile details were unavailable.');
  }

  const profile = (await response.json()) as FacebookGraphProfile;

  return {
    accessToken,
    email: cleanProfileString(profile.email, 254),
    fullName: cleanProfileString(profile.name, 80),
    photoURL: cleanProfileString(profile.picture?.data?.url, 2048),
  };
}

export function useFacebookAuthRequest() {
  const redirectUri = makeRedirectUri({
    native: `fb${facebookConfig.appId}://authorize`,
    path: 'auth/facebook/callback',
  });

  const [request, , promptAsync] = Facebook.useAuthRequest({
    androidClientId: facebookConfig.appId,
    clientId: facebookConfig.appId,
    redirectUri,
    scopes: ['public_profile', 'email'],
    iosClientId: facebookConfig.appId,
    webClientId: facebookConfig.appId,
  });

  async function promptForAccessToken() {
    const result = await promptAsync();

    if (result.type !== 'success') {
      throw new UserFacingError('Facebook sign-in was canceled.');
    }

    const token =
      result.authentication?.accessToken ??
      ('params' in result ? result.params.access_token : undefined);

    if (!token) {
      throw new Error('Facebook did not return an access token.');
    }

    return token;
  }

  async function promptForPrefillProfile() {
    const accessToken = await promptForAccessToken();
    return fetchFacebookPrefillProfile(accessToken);
  }

  return {
    disabled: !request,
    fetchPrefillProfile: fetchFacebookPrefillProfile,
    promptForAccessToken,
    promptForPrefillProfile,
  };
}
